package com.arenaproplay.admin.ui.components.admin

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.arenaproplay.admin.core.error.ApiException
import com.arenaproplay.admin.ui.theme.AppColors
import com.arenaproplay.admin.ui.theme.bodyMMedium
import com.arenaproplay.admin.ui.theme.bodyMRegular
import com.arenaproplay.admin.ui.theme.bodySMedium
import com.arenaproplay.admin.ui.utils.sBorderRadius
import kotlinx.coroutines.CancellationException
import org.jetbrains.compose.web.css.*

/**
 * Page-level load state shared by all admin pages.
 *
 * Pages key their `LaunchedEffect` on [reloadToken] and wrap the actual
 * repository calls in [load]; on failure [error] holds a human-readable
 * message and the page renders [ErrorState] instead of a spinner. The
 * Retry button calls [retry], which bumps [reloadToken] and re-runs the
 * effect.
 *
 * 401 handling stays untouched: token refresh and the login redirect are
 * driven by the Ktor Auth plugin inside
 * [com.arenaproplay.admin.data.http.HttpClientFactory] before any exception ever
 * reaches this layer, so catching errors here cannot break the logout flow.
 */
class LoadState {
    var error by mutableStateOf<String?>(null)
        private set

    var reloadToken by mutableStateOf(0)
        private set

    fun retry() {
        error = null
        reloadToken++
    }

    /**
     * Runs [block], recording any failure in [error].
     * [CancellationException] is rethrown so composition cancellation
     * (e.g. switching the period mid-flight) keeps working.
     */
    suspend fun load(block: suspend () -> Unit) {
        error = null
        try {
            block()
        } catch (e: CancellationException) {
            throw e
        } catch (@Suppress("TooGenericExceptionCaught") e: Throwable) {
            error = friendlyMessage(e)
        }
    }
}

@Composable
fun rememberLoadState(): LoadState = remember { LoadState() }

private fun friendlyMessage(e: Throwable): String = when (e) {
    is ApiException.NetworkError -> "Network error — check your connection and try again"
    is ApiException.AuthError -> "Session expired — please sign in again"
    is ApiException.ServerError -> "Server error (${e.statusCode}) — try again in a moment"
    is ApiException.ClientError -> e.message
    is ApiException.HttpError -> e.message
    is ApiException.ParseError -> "Unexpected response from the server"
    else -> e.message?.takeIf { it.isNotBlank() } ?: "Something went wrong"
}

/**
 * Full-width error block with a Retry button — the failure twin of the
 * DataTable empty/loading states and [com.arenaproplay.admin.ui.components.widgets.CardSpinner].
 */
@Composable
fun ErrorState(
    message: String,
    modifier: Modifier = Modifier,
    retryLabel: String = "Retry",
    onRetry: () -> Unit,
) {
    Box(
        modifier.fillMaxWidth().padding(topBottom = 48.px),
        contentAlignment = Alignment.Center,
    ) {
        Column(horizontalAlignment = Alignment.CenterHorizontally) {
            Box(
                Modifier.width(44.px).height(44.px).borderRadius(50.percent)
                    .styleModifier { property("background", "#2A1714") },
                contentAlignment = Alignment.Center,
            ) {
                SpanText(
                    "!",
                    Modifier.fontSize(20.px).color(AppColors.Error)
                        .styleModifier { property("font-weight", "700") },
                )
            }
            Box(Modifier.height(14.px))
            SpanText("Failed to load data", Modifier.bodyMMedium().color(AppColors.Ink))
            Box(Modifier.height(4.px))
            SpanText(message, Modifier.bodyMRegular().color(AppColors.Muted))
            Box(Modifier.height(18.px))
            Box(
                Modifier
                    .sBorderRadius(999)
                    .padding(leftRight = 20.px, topBottom = 10.px)
                    .cursor(Cursor.Pointer)
                    .styleModifier {
                        property("background", "#F0F4FC")
                        property("transition", "opacity 140ms ease")
                    }
                    .onClick { onRetry() },
            ) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    SpanText("↻", Modifier.fontSize(13.px).color(Color("#05080F")))
                    Box(Modifier.width(8.px))
                    SpanText(
                        retryLabel,
                        Modifier.bodySMedium().color(Color("#05080F"))
                            .styleModifier { property("font-weight", "600") },
                    )
                }
            }
        }
    }
}

/**
 * Convenience wrapper: renders [ErrorState] inside a [SectionCard] — the
 * common "whole page failed" presentation.
 */
@Composable
fun ErrorCard(state: LoadState) {
    val message = state.error ?: return
    SectionCard {
        ErrorState(message) { state.retry() }
    }
}
